// The search palette (⌘K, Ctrl K or /): invoices by number, client or
// amount, clients by name or email, and every page and action. Arrow keys
// move, Enter opens, Escape closes.
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '@/app/AppData';
import { contactLine, displayName } from '@/core/client';
import { today } from '@/core/dates';
import { invoiceMatchesQuery, isDraft, statusOn, totalsOf } from '@/core/invoice';
import { clientNameLabel } from '@/l10n/labels';
import { track } from '@/data/analytics';
import { t } from '@/l10n/strings';
import { StatusChip } from '@/ui/controls';
import { Icon, type IconName } from '@/ui/Icon';

interface Entry {
  key: string;
  group: string;
  icon: IconName;
  title: string;
  detail?: string;
  trailing?: React.ReactNode;
  to: string;
}

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const { invoices = [], clients = [], user } = useAppData();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();
  const returnFocus = useRef<Element | null>(document.activeElement);

  useEffect(() => {
    input.current?.focus();
    const back = returnFocus.current;
    return () => (back as HTMLElement | null)?.focus?.();
  }, []);

  const entries = useMemo<Entry[]>(() => {
    const q = query.trim().toLowerCase();
    const day = today();
    const matchingInvoices = (q ? invoices.filter((i) => invoiceMatchesQuery(i, q)) : invoices).slice(0, q ? 6 : 4);
    const matchingClients = q
      ? clients.filter((c) => [c.name, c.company, c.email].some((s) => s.toLowerCase().includes(q))).slice(0, 4)
      : [];
    const allPages: Entry[] = [
      { key: 'new-invoice', group: t('webPaletteActions'), icon: 'plus', title: t('shellNewInvoice'), to: '/invoices/new' },
      { key: 'new-client', group: t('webPaletteActions'), icon: 'userplus', title: t('clientFormNewClient'), to: '/clients/new' },
      ...(user?.isGuest ? [{ key: 'backup', group: t('webPaletteActions'), icon: 'cloudup' as IconName, title: t('webBackUp'), to: '/sign-in' }] : []),
      { key: 'home', group: t('webPalettePages'), icon: 'home', title: t('navDashboard'), to: '/home' },
      { key: 'invoices', group: t('webPalettePages'), icon: 'file', title: t('navInvoices'), to: '/invoices' },
      { key: 'overdue', group: t('webPalettePages'), icon: 'clock', title: t('webOverdueInvoices'), to: '/invoices?filter=overdue' },
      { key: 'clients', group: t('webPalettePages'), icon: 'users', title: t('navClients'), to: '/clients' },
      { key: 'design', group: t('webPalettePages'), icon: 'palette', title: t('webTitleDesign'), to: '/settings/design' },
      { key: 'profile', group: t('webPalettePages'), icon: 'store', title: t('webTitleProfile'), to: '/settings/profile' },
      { key: 'payment', group: t('webPalettePages'), icon: 'bank', title: t('settingsPaymentDetails'), to: '/settings/payment' },
      { key: 'settings', group: t('webPalettePages'), icon: 'gear', title: t('navSettings'), to: '/settings' },
    ];
    const pages = allPages.filter((e) => !q || e.title.toLowerCase().includes(q));
    return [
      ...matchingInvoices.map<Entry>((i) => ({
        key: `i-${i.id}`,
        group: t('navInvoices'),
        icon: 'file',
        title: `${i.number} · ${clientNameLabel(i.client)}`,
        detail: totalsOf(i).total.format(),
        trailing: <StatusChip status={statusOn(i, day)} />,
        to: isDraft(i) ? `/invoices/${i.id}/edit` : `/invoices/${i.id}`,
      })),
      ...matchingClients.map<Entry>((c) => ({ key: `c-${c.id}`, group: t('navClients'), icon: 'users', title: displayName(c), detail: contactLine(c) || undefined, to: `/clients/${c.id}` })),
      ...pages,
    ];
  }, [query, invoices, clients, user]);

  const at = entries.length ? Math.min(selected, entries.length - 1) : -1;

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [at]);

  const open = (entry: Entry) => {
    // The part of the app it leads to (invoices, clients…), never the result itself.
    track('palette_result_opened', { section: entry.to.split('/')[1] || 'home' });
    onClose();
    navigate(entry.to);
  };

  return (
    <div
      className="palette"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="palette__box" role="dialog" aria-modal="true" aria-label={t('webSearch')}>
        <div className="palette__input">
          <Icon name="search" />
          <input
            ref={input}
            value={query}
            placeholder={t('webSearchHint')}
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-activedescendant={at >= 0 ? `${id}-${at}` : undefined}
            aria-autocomplete="list"
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                if (entries.length) setSelected((s) => (s + (e.key === 'ArrowDown' ? 1 : -1) + entries.length) % entries.length);
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if (at >= 0) open(entries[at]);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
              } else if (e.key === 'Tab') {
                e.preventDefault();
              }
            }}
          />
          <span className="kbd">Esc</span>
        </div>
        {entries.length === 0 ? (
          <p className="body muted" style={{ padding: 24 }}>
            {t('webPaletteNoResults', { query: query.trim() })}
          </p>
        ) : (
          <ul className="palette__list" id={`${id}-list`} role="listbox" ref={list}>
            {entries.map((entry, i) => (
              <li key={entry.key} role="presentation">
                {(i === 0 || entries[i - 1].group !== entry.group) && (
                  <div className="palette__group" aria-hidden>
                    {entry.group}
                  </div>
                )}
                <div id={`${id}-${i}`} role="option" aria-selected={i === at} className="palette__item" onMouseMove={() => setSelected(i)} onClick={() => open(entry)}>
                  <span className="tile tile--grey" aria-hidden>
                    <Icon name={entry.icon} size={18} />
                  </span>
                  <span className="grow">
                    <b>{entry.title}</b>
                    {entry.detail && <span className="caption">{entry.detail}</span>}
                  </span>
                  {entry.trailing}
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="palette__foot" aria-hidden>
          <span>
            <span className="kbd">↑</span> <span className="kbd">↓</span> {t('webPaletteMove')}
          </span>
          <span>
            <span className="kbd">↵</span> {t('webPaletteOpen')}
          </span>
          <span>
            <span className="kbd">N</span> {t('shellNewInvoice')}
          </span>
        </div>
      </div>
    </div>
  );
}
